import React, {useState, useEffect} from 'react';

import { container, TYPES } from  '@/inversifyconfig/container.config';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { LockerAuthDetailsForUser, LockerFilesForUser } from  '@/interfaces/LockerDetailsApi';
import { LockerFile } from  '@/interfaces/LockerFile';
import { ToastData } from  '@/interfaces/LockerDetails';

import LockerDetailsSearch  from  '@/components/elements/LockerDetails/LockerDetailsSearch';
import LockerDetailsTable from  '@/components/elements/LockerDetails/LockerFilesTable';
import LockerDetailsResetRequest from  '@/components/elements/LockerDetails/LockerDetailsResetRequest';
import ToastNotification, { ToastMessageType } from  '@/components/elements/global/ToastNotification';
import ConfirmModal from  '@/components/modals/ConfirmModal';

import  '@/css/LockerDetails.scss';

interface LockerDetailsProps {
    username: string
    userId: string
}

const LockerDetails = ({ userId, username }:LockerDetailsProps) => {  
    const [lockerFiles, setLockerFiles] = useState<LockerFile[]>([])
    const [resetRequest, setResetRequest] = useState<Date>();
    const [filterOptions, setFilterOptions] = useState<{sort: string, sortBy: string}>({sortBy: 'Upload Date', sort: 'desc'})
    const [searchTerms, setSearchTerms] = useState<string>('')
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
    const [skip, setSkip] = useState<number>(0);
    const [totalFiles, setTotalFiles] = useState<number>(0);
    const [toast, setToast] = useState<ToastData>({show: false, message: undefined, type: undefined});
    const [lockerFilesLoading, setLockerFilesLoading] = useState<boolean>(true);
    const [hasLockerFiles, setHasLockerFiles] = useState<boolean>(false);

    const http = container.get<IHttpRequest>(TYPES.IHttpRequest); 

    function getQueryParams():{[key: string]: any} {

        const queryParams = {};
        const {sortBy, sort} = filterOptions;

        if (searchTerms) {
            queryParams[`SearchTerm`] = searchTerms;
        }

        if (sort && sortBy) {
            queryParams[`sortBy`] =  sortBy === 'Upload Date' ? 'CreateDate' : 'FileName' 
            queryParams['sortDirection'] = sort
        }

        return queryParams  
    } 

    async function restoreOrDelete(endpoint: string, action:string): Promise<any> {
        const httpMethod = action === "restore" ? "put" : "delete"
        return http[httpMethod](endpoint, {})
    }

    function getUserDetails() {
        http.get(`locker/users/${userId}/auth`)
        .then(({data: {passcodeResetRequestDate}}: { data: LockerAuthDetailsForUser}) => {
            setResetRequest(passcodeResetRequestDate ? new Date(passcodeResetRequestDate): undefined)
        })
        .catch(err => console.log(err));
    }

    function getLockerFiles(type: 'page' | 'refresh') {
        http.get(`locker/users/${userId}/files`, {params: {...getQueryParams(), skip: type === 'refresh' ? 0 : skip}})
        .then(({data}:{data: LockerFilesForUser}) => {
            if (data.totalFileCount > 0) {
                setHasLockerFiles(true);
            }
            setLockerFiles(type === 'refresh' 
                ? data.lockerFiles
                : [...lockerFiles, ...data.lockerFiles]
            );
            setSkip(data.nextSkip);
            setTotalFiles(data.totalFileCount)
            
        })
        .catch(err => {
            console.error("Locker Files Error: ", err)
        })
        .finally(() => setLockerFilesLoading(false));
    } 

    function handleModalConfirm(){
        restoreOrDelete(`locker/users/${userId}/reset`, "restore")
            .then(() => {
                setToast({
                    type: ToastMessageType.SUCCESS,
                    message: 'The user has been notified',
                    show: true
                })
                getUserDetails();
            })
            .catch(() => setToast({
                type: ToastMessageType.WARNING,
                message: 'We could not reset the users password at this time, please try again later or contact an administrator',
                show: true
            }))
            .finally(() => {
                setConfirmationModalOpen(false);
            });
    }

    useEffect(()=> {
        getUserDetails();
    },[])

    const {sortBy, sort} = filterOptions
    useEffect(() => {
        getLockerFiles('refresh');
    }, [searchTerms, sortBy, sort])

    if (lockerFilesLoading) {
        return <></>
    }


    return(
        <section id="locker-details">
            <ToastNotification show={toast?.show} closeToast={() => setToast({...toast, show: false})} messageType={toast?.type}>
                {toast?.message}
            </ ToastNotification>
            {hasLockerFiles && <LockerDetailsSearch searchTerm={searchTerms} setSearchValue={setSearchTerms}/>}
            
            <LockerDetailsTable 
                lockerFiles={lockerFiles}
                restoreOrDelete={restoreOrDelete}
                getLockerFiles={getLockerFiles}
                userId={userId}
                total={totalFiles}
                filterOptions= {filterOptions}
                setFilterOptions = {setFilterOptions}
                searchTerms= {searchTerms}
            /> 
      
            
            {resetRequest && (
                <>
                    <LockerDetailsResetRequest date={resetRequest} onButtonClick={()=> setConfirmationModalOpen(true)}/>
                    <ConfirmModal
                        modalConfig={{confirmText: 'Yes, restore'}}
                        isModalOpen={isConfirmationModalOpen}
                        setModalOpen={setConfirmationModalOpen}
                        onCancel={()=> setConfirmationModalOpen(false)}
                        onConfirm={handleModalConfirm}
                    >
                        <h3>
                            Are you sure you want to restore
                            <span className="locker-username-span"> {username}'s </span>
                            Locker?
                        </h3>
                    </ConfirmModal>
                </>
            )}
            
        </section>
    )
}

export default LockerDetails